'use client'

import type { ReactNode } from 'react'
import { AnalyticsSection } from './AnalyticsSection'
import {
  formatPeriod,
  formatRubles,
  formatShare,
  payoutShareBasisPoints,
  pluralOrders,
  type MarketMoneyReport,
} from '@/lib/market-money'

export const SALES_HERO_LABEL = 'Продажи'
export const RETURNS_LABEL = 'Возвраты'

export function flowPayoutShare(flow: MarketMoneyReport['flow']): number | null {
  if (flow.commissionShareBasisPoints !== null) return 10_000 - flow.commissionShareBasisPoints
  if (flow.salesKopecks === null || flow.payoutKopecks === null) return null
  return payoutShareBasisPoints(flow.salesKopecks, flow.payoutKopecks)
}

export function hasFlowAmounts(flow: MarketMoneyReport['flow']): boolean {
  return flow.availability !== 'unavailable' && flow.payoutKopecks !== null && flow.salesKopecks !== null
}

type Trend = 'up' | 'down' | 'flat'
type TrendTone = 'good' | 'bad' | 'neutral'

// Chips sit on the dark hero and on paper cards, so each tone carries its own readable ink.
const TONE_CLASSES: Record<TrendTone, string> = {
  good: 'bg-received/15 text-received',
  bad: 'bg-st-unpaid/15 text-st-unpaid',
  neutral: 'bg-bone text-gold-deep',
}

export function PeriodComparison({ currentKopecks, previousKopecks, period, higherIsBetter, noneText, testId, className, formatValue = formatRubles }: {
  currentKopecks: number
  previousKopecks: number
  period: string
  higherIsBetter: boolean
  noneText: string
  testId: string
  className: string
  formatValue?: (value: number) => string
}) {
  if (previousKopecks === 0) return <p data-testid={testId} className={className}>{noneText}</p>
  const trend: Trend = currentKopecks === previousKopecks ? 'flat' : currentKopecks > previousKopecks ? 'up' : 'down'
  const tone: TrendTone = trend === 'flat' ? 'neutral' : (trend === 'up') === higherIsBetter ? 'good' : 'bad'
  const change = Math.abs(((currentKopecks - previousKopecks) / previousKopecks) * 100)
  const chip = trend === 'flat'
    ? 'без изменений'
    : `${trend === 'up' ? '↑' : '↓'} ${change.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  return (
    <p data-testid={testId} className={`flex flex-wrap items-baseline gap-x-1.5 ${className}`}>
      <span data-testid={`${testId}-trend`} data-trend={trend} data-tone={tone} className={`tab-nums whitespace-nowrap rounded-full px-2 py-0.5 text-[12px] font-semibold ${TONE_CLASSES[tone]}`}>{chip}</span>
      {' '}
      <span className="tab-nums" title={period}>за предыдущий период · {formatValue(previousKopecks)}</span>
    </p>
  )
}

// A reference figure is never shown as a deduction: no minus sign and no negative colour.
type StatTone = 'default' | 'received' | 'unavailable' | 'reference'

const STAT_TONE_CLASSES: Record<StatTone, string> = {
  default: 'text-ink',
  received: 'text-received',
  unavailable: 'text-ink-faint',
  reference: 'text-ink',
}

export function StatCard({ label, value, note, tone, children }: { label: string; value: string; note?: string; tone: StatTone; children?: ReactNode }) {
  return (
    <article aria-label={label} data-availability={tone === 'unavailable' ? 'unavailable' : 'available'} className="surface-card col-span-1 flex flex-col gap-2.5 p-[18px] lg:col-span-2">
      <div className="eyebrow">{label}</div>
      <div data-testid="stat-value" data-tone={tone} className={`tab-nums whitespace-nowrap text-[22px] font-semibold lg:text-[19px] 2xl:text-[24px] ${STAT_TONE_CLASSES[tone]}`}>{value}</div>
      {note && <div className="text-[12px] leading-[1.45] text-ink-muted">{note}</div>}
      {children}
    </article>
  )
}

function SummaryGrid({ report }: { report: MarketMoneyReport }) {
  const { flow, sales, returns, previous } = report
  const share = flowPayoutShare(flow)
  const payoutsAvailable = hasFlowAmounts(flow)
  const payoutNote = payoutsAvailable && share !== null
    ? `${formatShare(share)} от продаж, за которые пришли выплаты${flow.availability === 'partial' ? ' · часть выплат ещё не пришла' : ''}`
    : 'нет выплат за период'
  const previousPeriod = previous ? formatPeriod(previous.from, previous.to) : null
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
      <article aria-label={SALES_HERO_LABEL} className="col-span-2 flex flex-col gap-3 bg-onyx px-6 py-[22px] text-ivory lg:col-span-6">
        <div className="eyebrow !text-ink-faint">{SALES_HERO_LABEL}</div>
        <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
          <span data-testid="summary-sales-amount" className="tab-nums whitespace-nowrap text-[34px] font-semibold tracking-[-0.02em] sm:text-[44px]">{formatRubles(sales.amountKopecks)}</span>
          <span data-testid="summary-sales-orders" className="text-[13px] text-ink-faint">
            {pluralOrders(sales.orders)}{previous ? ` · было ${previous.orders.toLocaleString('ru-RU')}` : ''}
          </span>
        </div>
        {previous === null && (
          <p data-testid="sales-comparison" data-trend="none" className="text-[13px] text-ink-faint">сравнить не с чем: прошлый такой же период раньше начала продаж</p>
        )}
        {previous && previousPeriod && (
          <PeriodComparison
            testId="sales-comparison"
            currentKopecks={sales.amountKopecks}
            previousKopecks={previous.amountKopecks}
            period={previousPeriod}
            higherIsBetter
            noneText="в предыдущем периоде продаж не было"
            className="text-[13px] text-gold-soft"
          />
        )}
      </article>
      <StatCard
        label="Выплаты Маркета"
        value={payoutsAvailable && flow.payoutKopecks !== null ? formatRubles(flow.payoutKopecks) : '—'}
        note={payoutNote}
        tone={payoutsAvailable ? 'received' : 'unavailable'}
      />
      <StatCard
        label="Средний чек"
        value={sales.averageCheckKopecks === null ? '—' : formatRubles(sales.averageCheckKopecks)}
        note={sales.averageCheckKopecks === null ? 'нет заказов за период' : 'по заказам периода'}
        tone={sales.averageCheckKopecks === null ? 'unavailable' : 'default'}
      />
      <StatCard label={RETURNS_LABEL} value={formatRubles(returns.amountKopecks)} tone="reference">
        {previous?.returns && previousPeriod && (
          <PeriodComparison
            testId="returns-comparison"
            currentKopecks={returns.amountKopecks}
            previousKopecks={previous.returns.amountKopecks}
            period={previousPeriod}
            higherIsBetter={false}
            noneText="в предыдущем периоде возвратов не было"
            className="text-[12px] text-ink-muted"
          />
        )}
      </StatCard>
    </div>
  )
}

export function MarketSummarySection({ report, loading, error, children }: {
  report: MarketMoneyReport | null
  loading: boolean
  error: string | null
  children?: ReactNode
}) {
  return (
    <AnalyticsSection
      id="summary"
      index="01"
      eyebrow="Сводка"
      title="Продажи за период"
      aside={report ? <span>{pluralOrders(report.sales.orders)}</span> : undefined}
    >
      {error && <div role="alert" className="border border-st-unpaid/25 bg-st-unpaid/8 px-4 py-3 text-[13px] text-st-unpaid">{error}</div>}
      {report ? (
        <div aria-busy={loading} className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <SummaryGrid report={report} />
        </div>
      ) : loading ? (
        <div role="status" className="surface-card flex h-[128px] items-center justify-center text-[13px] text-ink-faint">Загружаем продажи и выплаты…</div>
      ) : !error ? (
        <div className="surface-card p-5 text-[13px] text-ink-muted">Нет данных о продажах Маркета за этот период.</div>
      ) : null}
      {children}
    </AnalyticsSection>
  )
}
